import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { slides } from "@/mocks/slides";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);
  const current = slides[active];

  return (
    <section
      className="relative w-full h-[480px] sm:h-[540px] lg:h-[600px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点专题轮播"
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 专题配图`}
            title={`${slide.title} 吴梦梦影视专题`}
            className={`w-full h-full object-cover object-top transition-transform duration-[6500ms] ease-out ${
              index === active ? "scale-[1.06]" : "scale-100"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/30" />
        </div>
      ))}

      <div className="relative h-full container-content flex flex-col justify-center">
        <div className="w-full max-w-2xl pt-6">
          <span
            key={`kicker-${current.id}`}
            className="reveal inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-accent-500 text-primary-950 text-[11px] font-semibold tracking-wide"
          >
            <i className="ri-flashlight-fill text-[10px] leading-none" />
            {current.kicker}
          </span>

          <h1
            key={`title-${current.id}`}
            className="reveal mt-5 text-4xl sm:text-5xl lg:text-6xl font-black text-background-50 tracking-tight"
            style={{ animationDelay: "60ms" }}
          >
            {current.title}
          </h1>

          <p
            key={`sum-${current.id}`}
            className="reveal mt-4 md:mt-5 text-sm md:text-base text-background-100/85 leading-relaxed max-w-xl"
            style={{ animationDelay: "120ms" }}
          >
            {current.summary}
          </p>

          <div className="reveal mt-7 md:mt-8 flex flex-wrap items-center gap-3" style={{ animationDelay: "180ms" }}>
            <Link
              to={current.to}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              {current.cta}
              <i className="ri-arrow-right-line text-base leading-none" />
            </Link>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/35 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
            >
              浏览全部作品
            </Link>
          </div>
        </div>

        <div className="absolute left-4 md:left-6 bottom-8 md:bottom-10 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`切换至第 ${index + 1} 张：${slide.title}`}
                aria-current={index === active}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <span className="text-[11px] font-label text-background-200/80 tracking-[0.2em]">
            {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>

        <div className="absolute right-4 md:right-6 bottom-7 md:bottom-9 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label="上一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl leading-none" />
          </button>
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label="下一张"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl leading-none" />
          </button>
        </div>
      </div>
    </section>
  );
}